import React, { FC, useState } from "react";

import Image from "next/image";

import { CopyOutlined, DeleteOutlined, DownOutlined, UpOutlined } from "@ant-design/icons";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { t, Trans } from "@lingui/macro";
import { Button, Checkbox, Col, Divider, Input, Popconfirm, Row, Select, Space } from "antd";
import styled, { CSSProperties } from "styled-components";
import { v4 } from "uuid";

import ChoiceInput from "components/forms-view/inputs/choice-input";
import { CHOICE_FIELD_TYPES } from "components/forms-view/questions";

import { DraggableQuestion } from "utils/navirec-forms";

type QuestionItemProps = {
  question: DraggableQuestion;
  draggable?: boolean;
  disabled?: boolean;
  canDuplicate: boolean;
  additionalFieldOptions: {
    label: string;
    value: string | undefined;
    disabled: boolean;
  }[];
  fieldOptions: any[];
  copyQuestion: (id: string) => void;
  deleteQuestion: (id: string) => void;
  updateQuestion: (fieldName: string, value: any) => void;
  openAdditionalFieldModal: () => void;
};

const NewContactText = styled.div`
  color: #38d379;
  letter-spacing: 1px;
  font-weight: 700;
  font-size: 11px;
  cursor: pointer;
`;

const NewContactContainer = styled(Col)`
  padding: 8px 20px;
  border-top: solid 1px #eef2f8;
  justify-content: center;
  align-items: center;
`;

const StyledConatiner = styled.div<{ disabled: boolean }>`
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.06);
  padding: 16px;
  margin-bottom: 8px;
`;

const QuestionItem: FC<QuestionItemProps> = ({
  question,
  draggable = true,
  disabled = false,
  canDuplicate = true,
  additionalFieldOptions,
  fieldOptions,
  copyQuestion,
  deleteQuestion,
  updateQuestion,
  openAdditionalFieldModal,
}) => {
  const { attributes, isDragging, listeners, setNodeRef, setActivatorNodeRef, transform, transition } = useSortable({
    id: question.key,
    disabled: !draggable || disabled,
  });

  const [isExtended, setIsExtended] = useState<boolean>(!question.id);
  const [saveToAdditionalField, setSaveToAdditionalField] = useState<boolean>(!!question.additional_field);

  const style: CSSProperties = {
    opacity: isDragging ? 0.9 : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <StyledConatiner ref={setNodeRef} disabled={disabled} style={style}>
      <Row align={"middle"} gutter={16}>
        <Col
          ref={setActivatorNodeRef}
          {...listeners}
          {...attributes}
          style={{ justifyContent: "center", alignItems: "center", display: "flex", cursor: "pointer" }}
        >
          <Image alt="img" src={"/icons/drag.svg"} width={12} height={16} />
        </Col>
        <Col>
          <Select
            disabled={disabled}
            onSelect={(val) => {
              const newVal = question.choices || [{ id: v4(), value: "First choice" }];

              updateQuestion("choices", newVal);
              updateQuestion("type", val);
            }}
            style={{ width: 150 }}
            defaultValue={question.type}
          >
            {fieldOptions.map(({ key, label, icon, disabled }) => (
              <Select.Option disabled={disabled} key={key}>
                {
                  <span>
                    {icon} <span style={{ marginLeft: 4 }}>{label}</span>
                  </span>
                }
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col flex={1}>
          <Input
            disabled={disabled}
            autoFocus={!question.id}
            onBlur={(e) => updateQuestion("question", e.target.value)}
            defaultValue={question.question}
          />
        </Col>
        <Col>
          <Checkbox
            disabled={disabled}
            defaultChecked={!!question?.required}
            onChange={(e) => updateQuestion("required", e.target.checked)}
          >
            <Trans>Required</Trans>
          </Checkbox>
        </Col>
        {["vehicle_total_engine_time", "vehicle_odometer"].includes(question.type) ? (
          <Col>
            <Checkbox
              disabled={disabled}
              onChange={({ target: { checked } }) => {
                updateQuestion("autofill", checked);
              }}
              checked={question.autofill}
            >
              <Trans>Autofill</Trans>
            </Checkbox>
          </Col>
        ) : null}
        <Col style={{ justifyContent: "center", alignItems: "center", display: "flex" }}>
          <Button disabled={disabled} style={{ padding: "0px 12px" }} onClick={() => setIsExtended((val) => !val)}>
            <span>
              {isExtended ? (
                <UpOutlined style={{ fontSize: 12, marginBottom: 2 }} />
              ) : (
                <DownOutlined style={{ fontSize: 12 }} />
              )}
            </span>
          </Button>
        </Col>
      </Row>
      {isExtended ? (
        <div style={{ paddingTop: 16 }}>
          <Input
            defaultValue={question.description}
            onBlur={(e) => updateQuestion("description", e.target.value)}
            placeholder={t`Description (optional)`}
          />
          <Col>
            {CHOICE_FIELD_TYPES.includes(question.type) && question.choices && (
              <ChoiceInput
                moveChoice={(newVal) => updateQuestion("choices", newVal)}
                choices={question.choices}
                addChoice={() => updateQuestion("choices", [...(question?.choices || []), { id: v4(), value: "" }])}
                removeChoice={(choiceId: string) =>
                  updateQuestion(
                    "choices",
                    question.choices?.filter((x) => x.id !== choiceId)
                  )
                }
                updateChoiceValue={(choiceId: string, value: string) =>
                  updateQuestion(
                    "choices",
                    question.choices?.map((x) => (x.id === choiceId ? { ...x, value } : x))
                  )
                }
              />
            )}
          </Col>
          <Divider style={{ margin: "16px 0px" }} />
          <Row gutter={8} align={"middle"}>
            <Col>
              <Checkbox
                checked={saveToAdditionalField}
                onChange={({ target: { checked } }) => {
                  updateQuestion("additional_field", null);
                  setSaveToAdditionalField(checked);
                }}
              >
                <Trans>Save this answer to additional field</Trans>
              </Checkbox>
            </Col>
            <Col>
              <Select
                disabled={!saveToAdditionalField}
                style={{ width: 150 }}
                value={question.additional_field}
                onSelect={(val) => updateQuestion("additional_field", val)}
                placeholder={t`Select field`}
                options={additionalFieldOptions}
                dropdownRender={(menu) => (
                  <Col>
                    {menu}
                    <NewContactContainer>
                      <NewContactText onClick={() => openAdditionalFieldModal()}>
                        + <Trans>ADD NEW FIELD</Trans>
                      </NewContactText>
                    </NewContactContainer>
                  </Col>
                )}
              />
            </Col>
            <Col flex={1} />
            <Space size="large">
              <Button
                disabled={!canDuplicate}
                onClick={() => copyQuestion(question.key)}
                icon={
                  <span style={{ marginRight: 6 }}>
                    <CopyOutlined />
                  </span>
                }
              >
                {t`Duplicate question`}
              </Button>
              <Popconfirm
                title={t`Are you sure you want to delete this question?`}
                onConfirm={() => deleteQuestion(question.key)}
              >
                <Button
                  icon={
                    <span style={{ marginRight: 6 }}>
                      <DeleteOutlined />
                    </span>
                  }
                  danger
                >
                  {t`Delete question`}
                </Button>
              </Popconfirm>
            </Space>
          </Row>
        </div>
      ) : null}
    </StyledConatiner>
  );
};

export default React.memo(QuestionItem);
